import { useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system/lib/src';
import HTMLReactParser from 'html-react-parser';

import { selectMifInfo, selectUser } from 'store';

import CheckboxGroup from 'shared/CheckboxGroup';

import { useAppSelector } from 'hooks';
import { SexAtBirth } from 'utils/enums';

import ConfirmNoneOfHealthConcerns from './components/ConfirmNoneOfHealthConcerns';
import { MifQuestionOneProps } from './mifQuestionOne.types';

const MifQuestionOne: React.FC<MifQuestionOneProps> = ({ onContinue, config }) => {
  const { semaglutideMif } = useAppSelector(selectMifInfo);
  const { sexAtBirth } = useAppSelector(selectUser);

  const [isConfirmNoneOfHealthConcerns, toggleIsConfirmNoneOfHealthConcerns] = useToggle(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const options = config.options.filter(
    (el) => sexAtBirth === SexAtBirth.Female || el.value !== 'pms'
  );

  const handleSelect = (v: number | string) => {
    let newArray: string[] = [];
    const stringValue = String(v);
    if (v === 'none') {
      if (!selectedItems.includes('none')) {
        newArray = [v];
      }
    } else {
      newArray = selectedItems.includes(stringValue)
        ? selectedItems.filter((i) => i !== stringValue && i !== 'none')
        : [...selectedItems, stringValue].filter((i) => i !== 'none');
    }

    setSelectedItems(newArray);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedItems.includes('none')) {
      toggleIsConfirmNoneOfHealthConcerns(true);
    } else onContinue({ answer: selectedItems, question: config.question.value });
  };

  useEffect(() => {
    const selected =
      semaglutideMif.find((item) => item.question === config.question.value)?.answer ?? [];
    if (selected.length) {
      setSelectedItems(selected);
    }
  }, []);

  return (
    <form className="flex min-h-full flex-col gap-6" onSubmit={handleSubmit}>
      <Common.Modal
        close={() => toggleIsConfirmNoneOfHealthConcerns()}
        isOpen={isConfirmNoneOfHealthConcerns}
        size="sm"
      >
        <ConfirmNoneOfHealthConcerns
          onContinue={() => {
            onContinue({ answer: selectedItems, question: config.question.value });
            toggleIsConfirmNoneOfHealthConcerns();
          }}
          onClose={() => toggleIsConfirmNoneOfHealthConcerns()}
        />
      </Common.Modal>
      <h2 className="text-m2xl font-bold text-primary-700 md:text-2xl">
        {HTMLReactParser(config.question.label || '')}
      </h2>
      <Common.Alert type="info" colorableBackground>
        Note: Below, select all conditions that apply to help your provider confirm an appropriate
        personalized compound of semaglutide.
      </Common.Alert>
      <CheckboxGroup
        items={options.map(({ label, value, description }) => ({
          label: (
            <>
              <p>{label}</p>
              <p className="text-mSm text-gray md:text-sm">{description}</p>
            </>
          ),
          value
        }))}
        selectedItems={selectedItems}
        onSelect={handleSelect}
      />
      <Common.Button
        className="py-6 md:self-center"
        color="blue"
        disabled={!selectedItems.length}
        fullWidthOnMobile
      >
        Continue
      </Common.Button>
    </form>
  );
};

export default MifQuestionOne;
